import { SHORTENED_ERROR_MESSAGES } from '@/enums';
import { clearAuthStorage, getTokens, setTokensIntoLocalhost } from '@/utils';
import { notification } from 'antd';
import axios, { AxiosResponse } from 'axios';
import i18next from 'i18next';
import qs from 'qs';
import { AuthService } from './auth.service';

const APIService = axios.create({
  baseURL: process.env.REACT_APP_BACKEND_API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

export default APIService;

const refreshTokenErrorMessages = [
  'Refresh Token has been revoked',
  'Refresh Token has expired',
];

// State variables for handling token refresh and sign out requests
let refreshTokenRequest: Promise<any> | null = null;
let signOutRequest: Promise<any> | null = null;
let isTokenExpired = false;

// Function to handle getting new tokens
const handleGetNewTokens = AuthService.getNewTokens;

// Function to handle signing out
const handleSignOut = async () => AuthService.signOutWithoutResolve();

const regexUsedForCheckPathNeedToAttachCognitoInfo = [
  /^(\/)?users/,
  /^(\/)?auth/,
  /^(\/)?tenant-address/,
  /^(\/)?user-address/,
];

export const onRequestFulfilled = async (request: any) => {
  const token = JSON.parse(localStorage.getItem('auth') || '{}');
  const { accessToken: currentAccessToken } = token;

  // const tenantData = newQueryClient.getQueryData([QueryKey.SUB_DOMAIN]);

  // const tenant = get(tenantData, 'data', {}) as TenantEntity;

  // const { id: tenantId } = tenant;

  if (isTokenExpired) {
    refreshTokenRequest = refreshTokenRequest ?? handleGetNewTokens();

    try {
      const { data } = await refreshTokenRequest;
      const oldToken = getTokens();
      const { authenticationResult } = data || {};
      const newToken = { ...oldToken, ...authenticationResult };
      setTokensIntoLocalhost(newToken);

      request.headers.Authorization = `Bearer ${authenticationResult?.accessToken}`;
      return request;
    } catch {
      signOutRequest =
        signOutRequest ||
        handleSignOut().finally(() => {
          clearAuthStorage();
          signOutRequest = null;
        });
    } finally {
      isTokenExpired = false;
      refreshTokenRequest = null;
    }

    return;
  }

  if (!request.params) request.params = {};
  if (!request.headers) request.headers = {};
  if (currentAccessToken) {
    request.headers.Authorization = `Bearer ${currentAccessToken}`;
  } else {
    delete request.headers.Authorization;
  }
  if (request.url?.includes('countrystatecity')) {
    request.headers['X-CSCAPI-KEY'] = process.env.REACT_APP_X_CSCAPI_KEY;
    request.headers.Authorization = null;
  }
  request.baseURL = process.env.REACT_APP_BACKEND_API_URL;
  request.paramsSerializer = (params: Record<string, any>) =>
    qs.stringify(params);

  if (
    regexUsedForCheckPathNeedToAttachCognitoInfo.some(regex =>
      regex.test(request.url || ''),
    )
  ) {
    if (!request.params) request.params = {};
    // if (!request.params.tenantId && !!tenantId) {
    //   request.params.tenantId = tenantId;
    // }
  }
  return request;
};
APIService.interceptors.request.use(onRequestFulfilled, error =>
  Promise.reject(error),
);

export const onResponseFulfilled = (response: AxiosResponse) => response;
export const onResponseRejected = (error: any) => {
  const request = error.config;

  let data = error?.response?.data;

  if (typeof data === 'string') {
    try {
      data = JSON.parse(data);
    } catch (e) {
      console.error(e);
    }
  }

  const { statusCode, message = '', errorCode } = data || {};

  if (refreshTokenErrorMessages.includes(message)) {
    refreshTokenRequest = null;
    isTokenExpired = false;
    clearAuthStorage();
  }

  if (
    statusCode === 401 &&
    errorCode === SHORTENED_ERROR_MESSAGES.PASSWORD_EXPIRED
  ) {
    notification.error({ message: i18next.t('error.PASSWORD_EXPIRED') });
    handleSignOut().finally(() => {
      clearAuthStorage();
      signOutRequest = null;
    });
  } else if (statusCode === 401) {
    isTokenExpired = true;

    return APIService(request);
  } else {
    return Promise.reject(error);
  }
};
APIService.interceptors.response.use(onResponseFulfilled, onResponseRejected);
